/**
 * Almacenamiento a Domicilio — JSON-LD builders.
 *
 * Emits three schema.org objects (Service, LocalBusiness, BreadcrumbList) for
 * the on-demand landing pages. The exported helper is pure — pass in already-
 * resolved display strings from i18n, slugs from the URL, and an ordered
 * breadcrumb trail. The caller injects the returned objects via inline
 * `<script type="application/ld+json">` blocks (one per object).
 *
 * Design notes:
 *  - `areaServed` is intentionally omitted (not emitted as `[]`) when no
 *    coverage zones are provided. Empty arrays in JSON-LD are noisy and the
 *    Google Rich Results validator flags them.
 *  - `BreadcrumbList` positions are 1-indexed per schema.org spec.
 *  - The Service description is interpolated by the caller via i18n, so this
 *    builder doesn't need to know language. Same for `name`.
 */

const SPOTME_LOGO = 'https://www.spotme.mx/logos/favicon.ico'
const SPOTME_IMAGE = 'https://www.spotme.mx/images/cover.png'
const SPOTME_TELEPHONE = '+52-81-1821-6319'
const SPOTME_EMAIL = 'contacto@spotme.mx'

export interface IBreadcrumbItem {
  /** Human-readable name (already translated). */
  name: string
  /** Absolute URL for the crumb. */
  url: string
}

export interface IBuildJsonLdInput {
  /** Service display name (already translated). */
  serviceName: string
  /** Service description (already translated). */
  serviceDescription: string
  /** Absolute URL of the page being rendered. */
  pageUrl: string
  /** Canonical city slug (e.g. `'monterrey'`). Optional — parent page omits. */
  city?: string
  /** Human-readable city display name (e.g. `'Monterrey'`, `'Ciudad de México'`). */
  displayName?: string
  /** Coverage zones for `areaServed`. Empty array = key omitted. */
  coverageZones?: string[]
  /** Ordered breadcrumb trail. The last item is the current page. */
  breadcrumbTrail: IBreadcrumbItem[]
  /** Pricing anchor for the Service `offers` block (AggregateOffer). MXN.
   *  When omitted, the Service object skips `offers` entirely. Telling Google
   *  the explicit price range unlocks richer SERP snippets and lets local-pack
   *  results surface a "from $X" tag. */
  offers?: {
    /** Cheapest tier — typically `indicativePricing.small`. */
    lowPrice: number
    /** Most expensive tier listed indicatively on-page — typically
     *  `indicativePricing.large`. Beyond this we quote on call. */
    highPrice: number
    /** Number of indicative tiers — small/medium/large = 3. */
    offerCount: number
  }
}

interface IAreaServedItem {
  '@type': 'City'
  name: string
}

/**
 * Build the `Service` JSON-LD object for an on-demand landing page.
 *
 * Emits `areaServed` only when `coverageZones` has at least one entry. The
 * provider is always SpotMe Mexico.
 */
function buildServiceJsonLd(input: IBuildJsonLdInput): Record<string, unknown> {
  const { serviceName, serviceDescription, pageUrl, coverageZones, offers } =
    input

  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description: serviceDescription,
    url: pageUrl,
    serviceType: 'Door-to-door storage',
    provider: {
      '@type': 'Organization',
      name: 'SpotMe',
      url: 'https://www.spotme.mx',
      logo: SPOTME_LOGO
    }
  }

  if (coverageZones && coverageZones.length > 0) {
    const areaServed: IAreaServedItem[] = coverageZones.map((zone) => ({
      '@type': 'City',
      name: zone
    }))
    base.areaServed = areaServed
  }

  if (offers) {
    base.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'MXN',
      lowPrice: String(offers.lowPrice),
      highPrice: String(offers.highPrice),
      offerCount: offers.offerCount,
      availability: 'https://schema.org/InStock',
      url: pageUrl
    }
  }

  return base
}

/**
 * Build the `LocalBusiness` JSON-LD object.
 *
 * For city pages, adds `areaServed` mirroring the Service object so search
 * engines connect the local business to the served zones. For the parent page
 * (no city), emits the generic SpotMe LocalBusiness signal without zones.
 */
function buildLocalBusinessJsonLd(
  input: IBuildJsonLdInput
): Record<string, unknown> {
  const {
    serviceName,
    serviceDescription,
    pageUrl,
    displayName,
    coverageZones
  } = input

  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: displayName
      ? `SpotMe — ${serviceName} (${displayName})`
      : `SpotMe — ${serviceName}`,
    description: serviceDescription,
    url: pageUrl,
    image: SPOTME_IMAGE,
    logo: SPOTME_LOGO,
    telephone: SPOTME_TELEPHONE,
    email: SPOTME_EMAIL,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'MX'
    },
    sameAs: [
      'https://www.facebook.com/spotmemx',
      'https://www.instagram.com/spotme.mx',
      'https://www.linkedin.com/company/spotmemx'
    ]
  }

  if (coverageZones && coverageZones.length > 0) {
    const areaServed: IAreaServedItem[] = coverageZones.map((zone) => ({
      '@type': 'City',
      name: zone
    }))
    return { ...base, areaServed }
  }

  return base
}

/**
 * Build the `BreadcrumbList` JSON-LD object. Positions are 1-indexed.
 */
function buildBreadcrumbJsonLd(
  trail: IBreadcrumbItem[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      item: crumb.url
    }))
  }
}

/**
 * Build the `FAQPage` JSON-LD object — the FAQ rich result schema.
 *
 * Google requires plain-text answers (HTML is allowed but anchor markup
 * renders as literal text in the SERP carousel — strip embedded links).
 * Emit only when `faqItems` has at least one entry.
 */
function buildFaqJsonLd(
  items: { question: string; answer: string }[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

/**
 * Build the `HowTo` JSON-LD object — triggers Google's HowTo carousel for
 * the 4-step process (Agenda → Recolección → Almacenaje → Entrega). The
 * `name` should mirror the visible H2 above the steps.
 */
function buildHowToJsonLd(
  name: string,
  description: string,
  steps: { name: string; text: string }[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: step.name,
      text: step.text
    }))
  }
}

export interface IExtraJsonLdInput {
  /** FAQ items for the FAQPage schema. Pass empty/undefined to skip. */
  faqItems?: { question: string; answer: string }[]
  /** HowTo data for the 4-step process. Pass undefined to skip. */
  howTo?: {
    name: string
    description: string
    steps: { name: string; text: string }[]
  }
}

/**
 * Build all JSON-LD blocks for an on-demand landing page.
 *
 * Returns blocks in this order: Service, LocalBusiness, BreadcrumbList, then
 * (when supplied) FAQPage and HowTo. Each is a fully-formed schema.org
 * object suitable for serialization into a `<script type="application/ld+json">`
 * element.
 */
export function buildOnDemandJsonLd(
  input: IBuildJsonLdInput & IExtraJsonLdInput
): Record<string, unknown>[] {
  const blocks: Record<string, unknown>[] = [
    buildServiceJsonLd(input),
    buildLocalBusinessJsonLd(input),
    buildBreadcrumbJsonLd(input.breadcrumbTrail)
  ]

  if (input.faqItems && input.faqItems.length > 0) {
    blocks.push(buildFaqJsonLd(input.faqItems))
  }

  if (input.howTo && input.howTo.steps.length > 0) {
    blocks.push(
      buildHowToJsonLd(
        input.howTo.name,
        input.howTo.description,
        input.howTo.steps
      )
    )
  }

  return blocks
}
